import { useEffect, useMemo, useRef, useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import { postOfferView } from "../../../../services/offers/offers.service";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  PlacementStage,
  QueryPostOfferView,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import OfferItem from "../offer-item/OfferItem.component";

interface Props {
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  campaign: CampaignType;
  displayOnly: boolean;
  campaignId: string;
  placementId: string;
  placementName: string;
  emailSentIds: string[];
  sessionId: string;
  isSeen: boolean;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<boolean>;
  autoSendingOfferId: string;
}

const SwiperComponent = ({
  settings,
  emailSentIds,
  campaign,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  isSeen,
  openSingle,
  onAdd,
  sessionId,
  layoutId,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);
  const [numberOfColumns, setNumberOfColumns] = useState<number>(
    Number(settings?.mainLayout.offersNumber) || 3
  );

  // iframe width - 80px of slider arrows and 70px of padding
  const swiperWidth = document.body.offsetWidth;

  // swiper item that are fully displayed with margin 20px
  const swiperItemDisplayed = Math.floor(swiperWidth / 350);

  // swiper item that are fully displayed with last item without margin
  const swiperSize = Math.floor((swiperWidth - swiperItemDisplayed * 10) / 350);

  // swiper item that are half displayed with margin 20px
  const swiperItemForLoop = Math.ceil(swiperWidth / 360);

  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const lastTrackedRealIndexRef = useRef(0);
  const [showEmailSentToast, setShowEmailSentToast] = useState(false);
  const toastTimeoutRef = useRef<number | null>(null);

  const showEmailToast = () => {
    setShowEmailSentToast(true);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setShowEmailSentToast(false);
    }, 2000);
  };

  const handleAutoSendWithToast = async (offer: Offer, slot: number) => {
    const success = await onAutoSendEmailFromLabel(offer, slot);
    if (success) {
      showEmailToast();
    }
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const [noNavigation, setNoNav] = useState<boolean>(
    refNavNext.current?.classList.contains("swiper-button-lock") || false
  );

  const setDefaultOfferImpressionEvent = async (offers: OffersIdSlots[]) => {
    if (eventOfferIds.length < campaign.offers.length) {
      const data: QueryPostOfferView = {
        offers,
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId: layoutId,
      };

      await postOfferView(data);
    }
  };

  //set event for default active slide offers
  useEffect(() => {
    if (swiper && isSeen) {
      const _ids = campaign.offers
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: index + 1,
        }))
        .slice(
          0,
          settings?.mainLayout?.fixedSizeWithColumns
            ? numberOfColumns
            : swiperSize > 0
              ? swiperSize
              : 1
        );
      setDefaultOfferImpressionEvent(_ids);
      setEventOfferIds(_ids);
      swiper.update();
    }
    if (refNavNext.current) {
      setNoNav(refNavNext.current.classList.contains("swiper-button-lock"));
    }
    // eslint-disable-next-line
  }, [swiper, isSeen]);

  useEffect(() => {
    setNumberOfColumns(Number(settings?.mainLayout.offersNumber) || 3);
  }, [settings]);

  const hasPagination = useMemo((): boolean => {
    if (
      settings?.mainLayout?.fixedSizeWithColumns
        ? campaign.offers.length <= numberOfColumns
        : swiperItemDisplayed >= campaign.offers.length
    ) {
      return false;
    }
    return true;
  }, [
    numberOfColumns,
    campaign.offers.length,
    swiperItemDisplayed,
    settings?.mainLayout?.fixedSizeWithColumns,
  ]);

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!swiper) return;
      if (e.key === "ArrowLeft") {
        swiper.slidePrev();
      } else if (e.key === "ArrowRight") {
        swiper.slideNext();
      }
    };
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [swiper]);

  // Utility function to get new visible offers for impression event
  const getNewVisibleOffers = (activeIndex: number): void => {
    const offerCount = numberOfColumns;
    const offerIds = eventOfferIds.map((value: OffersIdSlots) => value.id);
    const newVisibleOffers: OffersIdSlots[] = [];
    for (let i = activeIndex; i < activeIndex + offerCount; i++) {
      if (
        campaign.offers[i] &&
        campaign.offers[i].id &&
        !offerIds.includes(campaign.offers[i].id)
      ) {
        newVisibleOffers.push({
          id: campaign.offers[i].id,
          slot: i + 1,
        });
      }
    }

    if (newVisibleOffers.length > 0) {
      setDefaultOfferImpressionEvent(newVisibleOffers);
      setEventOfferIds((prevState) => [...prevState, ...newVisibleOffers]);
    }
  };

  return (
    <>
      {campaign?.offers.length > 0 && isSeen && (
        <div
          className={`inline-swiper-wrapper ${noNavigation ? "no-navigation" : ""
            }`}
        >
          {showEmailSentToast && (
            <div className="template-email-toast">Email sent successfully</div>
          )}
          <Swiper
            style={{
              marginBottom: hasPagination ? "10px" : "0",
              padding: noNavigation ? "8px 0 10px 0" : "8px 0 40px 0",
            }}
            className={`swiper-container swiper-inline`}
            modules={[Navigation, Pagination]}
            watchSlidesProgress={true}
            pagination={{
              clickable: true,
              el: ".swiper-pagination",
            }}
            slidesPerView={
              settings?.mainLayout?.fixedSizeWithColumns
                ? numberOfColumns
                : "auto"
            }
            loop={
              settings?.mainLayout?.fixedSizeWithColumns
                ? campaign.offers.length > numberOfColumns
                : swiperItemForLoop < campaign.offers.length
            }
            spaceBetween={10}
            loopAddBlankSlides={true}
            onSwiper={(s: SwiperClass) => {
              setSwiper(s);
              lastTrackedRealIndexRef.current = s.realIndex;
            }}
            navigation={{
              prevEl: refNavPrev.current,
              nextEl: refNavNext.current,
            }}
            onBeforeInit={(swiper: any) => {
              swiper.params.navigation.prevEl = refNavPrev.current;
              swiper.params.navigation.nextEl = refNavNext.current;
            }}
            onTransitionEnd={(_swiper: SwiperClass) => {
              const activeIndex = _swiper.realIndex;
              if (activeIndex === lastTrackedRealIndexRef.current) return;
              lastTrackedRealIndexRef.current = activeIndex;
              getNewVisibleOffers(activeIndex);
            }}
          >
            {campaign?.offers.map((o: Offer, i: number) => {
              return (
                <SwiperSlide
                  key={i}
                  style={{
                    color: settings?.dealLayout.textColor,
                  }}
                >
                  <OfferItem
                    offer={o}
                    slot={i + 1}
                    emailSentIds={emailSentIds}
                    settings={settings}
                    isAdded={o.isAdded || false}
                    settingsTemplate={settingsTemplate}
                    onAdd={onAdd}
                    displayOnly={displayOnly}
                    campaign={campaign}
                    retailerId={campaign?.company_Id}
                    campaignId={campaignId}
                    placementId={placementId}
                    placementName={placementName}
                    openSingle={openSingle}
                    hasVoucher={campaign.offers.some(
                      (offer) => offer.voucher && offer.voucher.voucherType
                    )}
                    otherSettings={settings.otherSettings}
                    sessionId={sessionId}
                    layoutId={layoutId}
                    layoutOptions={settings.layoutOptions}
                    canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                    canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                    onAutoSendEmailFromLabel={handleAutoSendWithToast}
                    autoSendingOfferId={autoSendingOfferId === o.id}
                  />
                </SwiperSlide>
              );
            })}
            <div className="swiper-navigation">
              <div
                className="swiper-button-prev"
                ref={refNavPrev}
                tabIndex={0}
                role="button"
                aria-label="Previous offers"
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft") {
                    swiper?.slidePrev();
                  }

                  if (e.key === "Enter") {
                    swiper?.slidePrev();
                  }
                }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="17"
                    cy="17"
                    r="17"
                    transform="rotate(-180 17 17)"
                    fill={settings.dealLayout.backgroundColor}
                  />
                  <rect
                    x="18.6094"
                    y="23"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-135 18.6094 23)"
                    fill={settings.dealLayout.textColor}
                  />
                  <rect
                    x="20.3135"
                    y="12.2188"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(135 20.3135 12.2188)"
                    fill={settings.dealLayout.textColor}
                  />
                </svg>
              </div>
              <div className="swiper-pagination"></div>
              <div
                className="swiper-button-next"
                ref={refNavNext}
                tabIndex={0}
                role="button"
                aria-label="Next offers"
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") {
                    swiper?.slideNext();
                  }

                  if (e.key === "Enter") {
                    swiper?.slideNext();
                  }
                }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="17"
                    cy="17"
                    r="17"
                    fill={settings.dealLayout.backgroundColor}
                  />
                  <rect
                    x="15.3906"
                    y="11"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(45 15.3906 11)"
                    fill={settings.dealLayout.textColor}
                  />
                  <rect
                    x="14.1719"
                    y="22.2656"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-45 14.1719 22.2656)"
                    fill={settings.dealLayout.textColor}
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        </div>
      )}
    </>
  );
};

export default SwiperComponent;
